import {Component, ElementRef, HostListener, OnInit, ViewChild} from '@angular/core';
import {FormModel} from "../form/form.model.abstract";
import {FormConfig} from "../form/form.config";
import {StepComponent} from "../step/step.component";
import {StepService} from "../services/step.service";
import {Router} from "@angular/router";
import {ElementService} from "../services/element.service";
import {PortalService} from "../services/portal.service";
import {VerbrauchsanalyseForm} from "./verbrauchsanalyse.form";
import ol from "ol/dist/ol";
import control = ol.control;


/**
 * Custom step for the input of invoices on the step "verbrauchsdaten"
 *
 * @inheritDoc
 */
@Component({
  selector: 'app-step-verbrauchsanalyse-verbrauchsdaten',
  templateUrl: './verbrauchsanalyse.step.verbrauchsdaten.component.html'
})
export class VerbrauchsanalyseStepVerbrauchsdatenComponent extends StepComponent implements OnInit {
  /**
   * Maximum number of invoices.
   */
  readonly MAX_INVOICES = 5;

  /**
   * array of the formfields of each invoiceform we want to show
   */
  invoices: any = [];

  /**
   * toggle that is true while sending/receiving data to/from backend
   */
  loadingData: boolean = false;

  /**
   * list of backend errors to be shown on the frontend
   */
  backendErrors: {[key: number]: string[]} = {};

  /** placeholders for the error messages that come from the backend.
   */
  backendErrorsPlaceholder: {[key: string]: string} = {};

  /**
   * List of errors for the single invoices.
   */
  invoiceErrors: {[key: number]: {error: string, fields: string[]}} = {};

  /**
   * The wrapper for the stepform
   */
  @ViewChild('formWrapper') formWrapper: ElementRef;

  /**
   * Holds the URL to the entrystep
   */
  entryUrl: string;

  /**
   * Requestservice to talk with the backend
   * @protected
   */
  protected requestService: any;

  /**
   * anfrage object
   */
  anfrage: any;

  /**
   * @inheritDoc
   */
  formConfig: VerbrauchsanalyseForm;

  /**
   * @inheritDoc
   * @param stepService
   * @param router
   * @param elementService
   * @param portalService
   */
  constructor(protected stepService: StepService, protected router: Router, protected elementService: ElementService,
              protected portalService: PortalService) {
    super(stepService, router, elementService);
  }

  /**
   * @inheritDoc
   */
  ngOnInit() {
    super.ngOnInit();
    this.formConfig.anfrage = this.anfrage;

    // set the energy source as postfix for label and help texts
    const postfix = this.anfrage.verbrauchsanalyseInput.energietraeger_name.replace(/waermepumpe_.+/, 'waermepumpe');
    this.formConfig.setLabelPostfix(postfix);
    this.formConfig.setInfoPostfix(postfix, ['energietraeger_verbrauch', 'energietraeger_kosten']);

    // if there are changes in the form, reset the warnings
    this.subscriptions.push(this.form.valueChanges.subscribe(() => {
      // if there are changes in the form, reset the warnings
      this.formWarning = null;

      /* Enable/disable some input field for warm water and additional heating.
       * these fields are directly on this step and have dynamic names. Therefore,
       * they cannot be enabled/disabled in the form config
       */
      for(let i = 0; i < this.MAX_INVOICES; i++) {
        const idPrefix = i === 0 ? '' : i;

        // additional heating
        const hasZusatzheizung = this.anfrage.verbrauchsanalyseInput?.zusatzheizung_vorhanden;
        const isMfh = this.anfrage.verbrauchsanalyseInput?.gebaeudetyp === 'mehrfamilienhaus';
        ['zusatzheizung_verbrauch', 'zusatzheizung_einheit'].forEach(field => {
          if (isMfh === true || hasZusatzheizung !== 'ja' || this.form.controls['zusatzheizung_verbrauch_bekannt' + idPrefix + '_nein']?.value === true) {
            this.form.controls[field + idPrefix]?.disable({onlySelf: false, emitEvent: false});

            if(field.includes('_verbrauch') && this.form.controls[field + idPrefix]?.value > 0) {
              this.form.controls[field + idPrefix]?.setValue(null, {emitEvent: false});
              this.anfrage.verbrauchsanalyseInput[field + idPrefix] = null;
            }
          }
          else {
            this.form.controls[field + idPrefix]?.enable({onlySelf: false, emitEvent: false});
          }
        });

        // warm water
        const warmwasserMessung = this.anfrage.verbrauchsanalyseInput.warmwasser_messung;
        ['warmwasser_verbrauch', 'warmwasser_einheit', 'warmwasser_temperatur'].forEach(field => {
          if (warmwasserMessung !== 'gemessen' || this.form.controls['warmwasser_verbrauch_bekannt' + idPrefix + '_nein']?.value === true) {
            this.form.controls[field + idPrefix]?.disable({onlySelf: false, emitEvent: false});

            if((field.includes('_verbrauch') || field.includes('_temperatur')) && this.form.controls[field + idPrefix]?.value > 0) {
              this.form.controls[field + idPrefix]?.setValue(null, {emitEvent: false});
              this.anfrage.verbrauchsanalyseInput[field + idPrefix] = null;
            }
          }
          else {
            this.form.controls[field + idPrefix]?.enable({onlySelf: false, emitEvent: false});
          }
        });
      }
    }));
  }

  /**
   * initialize the fields for the invoices.
   */
  initInvoices(): void {
    // add the default fields to the local invoice array
    this.invoices.push(this.formConfig.getFieldsFromGroup(this.steps.currentStep.name));

    // check if we have data for more invoices, if yes: add more
    while(true) {
      if(this.anfrage.verbrauchsanalyseInput.hasOwnProperty('abrechnung_bis' + this.invoices.length) === false) {
        // stop here, no more data left
        break;
      }

      // add one more invoice
      this.addInvoiceFormFields();
    }
  }

  /**
   * @inheritDoc
   * @param data
   */
  saveData(data:{[key: string]: any}) {
    // merge the data with the existing data
    this.anfrage.verbrauchsanalyseInput = {...this.anfrage.verbrauchsanalyseInput, ...data};
    this.requestService.setAnfrage(this.anfrage);

    // save the data also to the server
    this.requestService.saveDataToServer();

    super.saveData(data);
  }

  /**
   * show form errors if the form should be submitted
   */
  showFormErrors(): void {
    this.formError = 'emptyfield';

    // check if there are formerrors (validors on the whole form) set, if yes, show them
    if(this.form.errors) {
      this.formConfig.invalidateFieldsOfFormvalidators(this.form.errors);
      Object.keys(this.form.errors).map(key => this.formError = key);
    }
    else {
      // set the first error of an input field
      Object.keys(this.form.controls).forEach(fieldName => {
        if(this.form.controls[fieldName]?.errors !== null) {
          this.formError = Object.keys(this.form.controls[fieldName].errors)[0];
          return;
        }
      });
    }

    // stop here, form is not valid
    if(this.formWrapper?.nativeElement) {
      this.elementService.scrollToElement(this.formWrapper.nativeElement);
    }
  }

  /**
   * add another invoice to the form
   */
  addInvoice(): void {
    this.resetErrors();

    if(!this.form.valid) {
      // do nothing, form must be valid
      this.showFormErrors();
      return;
    }

    // save data
    this.saveData(this.formConfig.getData());

    // reset errors
    this.resetErrors();

    // get the current invoiceindex to be checked
    const invoiceIndex = this.invoices.length - 1;

    // start loading indicator
    this.loadingData = true;

    // validate the last invoice
    this.validateInvoice(invoiceIndex).then(result => {
      // stop loading
      this.loadingData = false;

      if(result?.status?.code === 0) {
        this.addInvoiceFormFields();

        // scroll to the next invoice
        setTimeout(() => this.elementService.scrollToElement(document.querySelector('.invoice-' + (invoiceIndex + 1))), 200);
      }
      else {
        this.showValidationErrors(result, invoiceIndex);
      }
    });
  }

  /**
   * one invoice is delete from the form. Remove the fields and update the validility
   * @param invoiceIndex
   */
  removeInvoice(invoiceIndex: number): void {
    // remove fields from form and locally saved data
    this.invoices[invoiceIndex].forEach(field => {
      // delete localdata
      field.getIds().forEach(
        id => {
          this.form.removeControl(id);
          delete this.anfrage.verbrauchsanalyseInput[id]
        }
      );
    });

    // update the anfragedata to local storage and on the server
    this.requestService.setAnfrage(this.anfrage);
    this.requestService.saveDataToServer();

    // remove from local array
    this.invoices.splice(invoiceIndex, 1);

    // update the height
    setTimeout(() => {
      // scroll to top
      this.elementService.scrollToElement(document.querySelector('.invoice-' + (invoiceIndex - 1)));

      // inform the stepservice that the height changes
      this.stepService.heightChanged.emit();
    }, 0);
  }

  /**
   * for a new invoice: clone all formfields from the default fields and add them
   */
  addInvoiceFormFields(): void {
    const fields: FormModel[] = [];

    // get all default fields
    this.formConfig.getFieldsFromGroup(this.steps.currentStep.name).forEach((field) => {
      // ignore inactive fields
      if(field.isActive() === false) {
        return;
      }

      // clone the field and change the id
      const newField = field.clone();
      newField.setIdPostfix(this.invoices.length);
      newField.setValidators(field.getValidators());
      newField.setClasses(field.getClasses());

      // set some special attributes
      newField.setTeaser(field.hasTeaser);

      // add the field to the current form for validation
      this.formConfig.addFieldToForm(this.form, newField);
      fields.push(newField);
    });

    // preset datefields if they are not already there
    if(this.anfrage.verbrauchsanalyseInput.hasOwnProperty('abrechnung_von' + this.invoices.length) === false &&
      this.anfrage.verbrauchsanalyseInput.hasOwnProperty('abrechnung_bis' + this.invoices.length) === false) {
      this.anfrage.verbrauchsanalyseInput['abrechnung_von' + this.invoices.length] = FormConfig.getFirstDayOfLastYear(this.invoices.length);
      this.anfrage.verbrauchsanalyseInput['abrechnung_bis' + this.invoices.length] = FormConfig.getLastDayOfLastYear(this.invoices.length);
    }

    // preset values if there are some
    this.formConfig.setData(this.anfrage.verbrauchsanalyseInput);

    // add fields to local array to show the form
    this.invoices.push(fields);

    // update the height
    setTimeout(() => this.stepService.heightChanged.emit(), 0);
  }

  /**
   * @inheritDoc
   */
  async onSubmit() {
    this.resetErrors();

    if(this.form.valid === false) {
      this.showFormErrors();
      return;
    }

    // save the input up to here
    this.saveData(this.formConfig.getData());

    // start loading animation
    this.loadingData = true;

    // if more than one invoice, validate each invoice on its own
    if(this.invoices.length > 1) {
      // validate each invoice and wait for all request to stop
      const results = await Promise.all(this.invoices.map((invoice, index) => this.validateInvoice(index)));

      // stop loading animation
      this.loadingData = false;

      // check if we have faulty invoices
      const firstInvalidInvoiceIndex = results.findIndex(invoiceResult => !((<any>invoiceResult)?.status?.code === 0));
      if(firstInvalidInvoiceIndex > -1) {
        this.formWarning = null;
        // show error message for invoice
        this.showValidationErrors(results[firstInvalidInvoiceIndex], firstInvalidInvoiceIndex);
        // scroll to the faulty invoice
        this.elementService.scrollToElement(this.stepForm.nativeElement.querySelector('.invoice-' + firstInvalidInvoiceIndex));
        return;
      }
    }

    // send full data to the backend and start the calculation
    this.validateInvoice().then(result => {
      this.loadingData = false;

      // check response from the backend
      if(result?.status?.code === 0) {
        // save result and move to the next step
        this.anfrage.verbrauchsanalyseResult = result;
        this.requestService.setAnfrage(this.anfrage);

        // check if there is a warning and if we want to sho this
        if(result.status.warnings.length > 0 && !this.formWarning && result.status.warnings[0].code) {
          this.formWarning = result.status.warnings[0].code;
          this.elementService.scrollToElement(this.formWrapper.nativeElement);
          return;
        }

        // show next step
        this.moveToNextStep();
        return;
      }
      else {
        // there is an error
        this.formWarning = null;
        this.showValidationErrors(result, 0);
      }
    });
  }

  /**
   * resets the visible errors in all invoices
   */
  resetErrors(): void {
    this.formConfig.resetFormValidatorsFromFields();
    this.formError = null;
    this.invoiceErrors = {};
    this.backendErrors = {};
  }

  /**
   * validates one single invoice with the backend.
   * @param invoiceIndex
   */
  validateInvoice(invoiceIndex: number = null): Promise<any> {
    return new Promise(resolver => {
      this.requestService.backendService.getRequestId().subscribe(requestId => {
        let input = this.anfrage.verbrauchsanalyseInput;
        this.requestService.backendService.verbrauchsanalyse(input, requestId, invoiceIndex).subscribe(response => {
          // check response from the backend
          resolver(response.hasOwnProperty('result')? response.result : {});
        }, error => {
          resolver(error);
        });
      });
    });
  }

  /**
   * Shows validatiomn error on one single invoice.
   * @param result
   * @param invoiceIndex
   */
  showValidationErrors(result: any, invoiceIndex: number): void {
    // reset errors
    delete this.invoiceErrors[invoiceIndex];

    if(result?.status === 400) {
      this.invoiceErrors[invoiceIndex] = {error: 'input', fields: result.error?.result?.status?.errors[0]?.affected_fields || []};
      // show specific error message, based on backend reply
      this.backendErrors[invoiceIndex] = result.error?.result?.status ? result.error.result.status.errors.map(error => {
        return this.textkeyPrefix + '.verbrauchsanalyse.verbrauchsdaten.error.' + error.code;
      }) : [];
      // remove duplicate entries
      this.backendErrors[invoiceIndex] = [...new Set(this.backendErrors[invoiceIndex])];
    }
    else {
      // generic error, mostly on the connection to the backend.
      this.formError = 'connection';
    }

    this.elementService.scrollToElement(document.querySelector('.invoice-' + invoiceIndex));
  }

  /**
   * Show error mark on affected fields of invalid forms
   * @param invoiceIndex
   * @param field
   */
  isAffectedFieldForError(invoiceIndex: number, field: FormModel): boolean {
    // check if at least on of the field ids is in the list of affected fields
    if(this.invoiceErrors[invoiceIndex]) {
      // remove all numbers from the fieldnames
      const fieldIds = field.getIds().map(id => id.replace(/\d/, ''));
      // remove some extra string from the affected fields
      const fieldNames = this.invoiceErrors[invoiceIndex].fields.map(fieldname => fieldname.replace('abrechnungen.0.', '').replace('.', '_'));

      // check if one of the ids is in the affected field list
      return fieldIds.filter(id => fieldNames.indexOf(id) > -1).length > 0;
    }
    return false;
  }

  /**
   * Build some text placeholders for the (api) error messages
   */
  backendErrorPlaceholder(): any {
    let flaeche = 0;
    if(this.anfrage.verbrauchsanalyseInput.hasOwnProperty('gebaeude_flaeche') && this.anfrage.verbrauchsanalyseInput.gebaeude_flaeche > 0) {
      flaeche = this.anfrage.verbrauchsanalyseInput.gebaeude_flaeche;
    }
    else if(this.anfrage.verbrauchsanalyseInput.hasOwnProperty('wohnung_flaeche')) {
      flaeche = this.anfrage.verbrauchsanalyseInput.wohnung_flaeche;
    }

    this.backendErrorsPlaceholder = {...this.anfrage.verbrauchsanalyseInput,
      ...{portalId: this.portalService.currentPortalId}, ...{flaeche}};

    // get the building year from the api for the error message
    this.backendErrorsPlaceholder.gebaeudebaujahr = this.anfrage.verbrauchsanalyseInput.gebaeudebaujahr;

    return {
      ...this.anfrage.verbrauchsanalyseInput,
      ...this.anfrage.verbrauchsanalyseResult.placeholder,
      ...{entryUrl: this.entryUrl},
      ...{portalId: this.portalService.currentPortalId},
      ...{
        flaeche,
        gebaeudebaujahr: this.anfrage.verbrauchsanalyseInput.gebaeudebaujahr,
        fallback: 'step.error.' + this.formError
      }
    };
  }

}
